import { gql } from '@apollo/client';
import { theme } from '@arnold/common';
import { graphql } from '@apollo/client/react/hoc';
import styled from '@emotion/styled/macro';
import { clone, differenceWith } from 'ramda';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { InputConsumer } from '../chat/InputContext';
import { Answer, GetMessagesQuery } from '../generated/hooks';
import { channelQuery, messagesQuery } from '../graphql/queries';
import { IChannel } from '../types';

const EditMessage = styled.button`
  margin-left: 18px;
  font-size: 12px;
  font-weight: 500;
  color: ${theme.colors.actionPrimary.default};
  white-space: nowrap;
  text-decoration: none;
  border: none;
  padding: 0;
  background: none;
  outline-color: transparent;
  outline: none;

  :hover {
    text-decoration: underline;
    cursor: ${({ onClick }) => (onClick ? 'pointer' : 'inherit')};
  }

  :disabled {
    color: #a1a1a1;
    cursor: auto;
    text-decoration: none;
  }
`;

interface IProps extends WithTranslation {
  message: NonNullable<GetMessagesQuery['messages']>[0];
  disabled?: boolean;
  mutate?: (conf: any) => Promise<any>;
  channel: IChannel;
  editInputText: (type: string, text: string, values?: number[]) => void;
  t: any;
  clearInput: () => void;
  setRemainingQuestions: (remainingQuestions: number) => void;
  // scale prefilling API from InputContext
  prefillScaleState: (selections: (number | null)[], comments: (string | null)[]) => void;
}

type MessageTypeSelectAnswerWithQuestionDefinitions = Extract<IProps['message'], { __typename?: 'SelectAnswer' }>;
type QuestionDefinitionOfSelectAnswer = Extract<
  MessageTypeSelectAnswerWithQuestionDefinitions['question']['definition'],
  { __typename?: 'SelectQuestionDefinition' }
>;
type MessageTypeSelectAnswer = MessageTypeSelectAnswerWithQuestionDefinitions & {
  question: {
    definition: QuestionDefinitionOfSelectAnswer;
  };
};

class EditBubble extends React.Component<IProps> {
  handleClick = () => {
    this.props.clearInput();
    const { mutate, message, channel, editInputText } = this.props;
    if (message && mutate) {
      mutate({
        variables: {
          channelId: channel.id,
          fromMessageId: message.id,
        },
        update: (proxy: any, { data: { deleteChannelMessages } }: any) => {
          const deletedMessages = deleteChannelMessages.deletedMessages;
          this.props.setRemainingQuestions(deleteChannelMessages.remainingQuestions);
          const data = clone(
            proxy.readQuery({
              query: messagesQuery,
              variables: { channelId: channel.id },
            }),
          );
          const newMessages = differenceWith(
            (x: NonNullable<GetMessagesQuery['messages']>[0], y: NonNullable<GetMessagesQuery['messages']>[0]) =>
              x.id === y.id,
            data.messages,
            deletedMessages,
          );
          data.messages = newMessages;
          const currentMessages = data.messages as NonNullable<GetMessagesQuery['messages']>;
          const lastMessage = currentMessages[currentMessages.length - 1];
          if (
            lastMessage?.__typename === 'Question' &&
            lastMessage.hasOwnProperty('definition') &&
            (lastMessage as any).definition.__typename === 'ScaleQuestionDefinition'
          ) {
            const lastMessageQuestionId = lastMessage.id;
            const deletedMessagesForLastQuestion = deletedMessages.filter(
              (message: Answer) => message.question?.id === lastMessageQuestionId,
            );

            try {
              const statementsLengthFromQuestion = (lastMessage as any)?.definition?.statements?.length;
              const maxIndex = deletedMessagesForLastQuestion.reduce((acc: number, deletedMessage: any) => {
                const index = typeof deletedMessage?.statementIndex === 'number' ? deletedMessage.statementIndex : -1;
                return index > acc ? index : acc;
              }, -1);
              const statementsLength =
                typeof statementsLengthFromQuestion === 'number' && statementsLengthFromQuestion > 0
                  ? statementsLengthFromQuestion
                  : maxIndex + 1;

              if (statementsLength > 0) {
                const selections: (number | null)[] = Array(statementsLength).fill(null);
                const comments: (string | null)[] = Array(statementsLength).fill(null);
                deletedMessagesForLastQuestion.forEach((deletedMessage: any) => {
                  const index =
                    typeof deletedMessage?.statementIndex === 'number' ? deletedMessage.statementIndex : null;
                  if (index != null && index >= 0 && index < statementsLength) {
                    selections[index] = Array.isArray(deletedMessage?.value) ? deletedMessage.value[0] ?? null : null;
                    comments[index] = (deletedMessage?.comment as string | null) ?? null;
                  }
                });
                this.props.prefillScaleState(selections, comments);
              }
            } catch (e) {
              // eslint-disable-next-line no-console
              console.error('Failed to prefill scale state from deleted messages after click on edit bubble', e);
            }
          }
          const channelData = clone(
            proxy.readQuery({
              query: channelQuery,
              variables: { channelId: channel.id },
            }),
          );
          if (channelData) {
            channelData.channel.isFinished = false;
            proxy.writeQuery({
              query: channelQuery,
              data: channelData,
              variables: { channelId: channel.id },
            });
          }
          proxy.writeQuery({
            query: messagesQuery,
            data,
            variables: { channelId: channel.id },
          });
        },
      }).then(() => {
        const value =
          message.hasOwnProperty('question') &&
          (message as MessageTypeSelectAnswer).question.definition.selectedRange &&
          (message as MessageTypeSelectAnswer).question.definition.selectedRange[1] > 1
            ? (message as MessageTypeSelectAnswer).value
            : undefined;
        editInputText(message.__typename!, message.text || '', value);
      });
    }
  };

  render() {
    const { disabled } = this.props;
    return (
      <EditMessage onClick={!disabled ? this.handleClick : undefined} data-cy="chat-edit-button" disabled={disabled}>
        {this.props.t('changeAnswer')}
      </EditMessage>
    );
  }
}

const deleteChannelMessagesMutation = gql`
  mutation deleteChannelMessages($channelId: ID!, $fromMessageId: ID!) {
    deleteChannelMessages(channelId: $channelId, fromMessageId: $fromMessageId) {
      deletedMessages {
        id
        text
        createdBy
        ... on ScaleAnswer {
          value
          statementIndex
          comment
          question {
            id
          }
        }
      }
      remainingQuestions
    }
  }
`;

const WithdeleteOperation = graphql<Omit<IProps, 'mutate'>>(deleteChannelMessagesMutation, {
  name: 'mutate',
});

const EditBubbleWithOperations = withTranslation('editBubble')(WithdeleteOperation(EditBubble));

const GetPropsFromProvider = ({
  channel,
  message,
  disabled,
}: {
  disabled?: boolean;
  channel: IChannel;
  message: NonNullable<GetMessagesQuery['messages']>[0];
}) => (
  <InputConsumer>
    {(context) => (
      <EditBubbleWithOperations
        channel={channel}
        editInputText={context.editInputValue}
        message={message}
        disabled={disabled}
        clearInput={context.clearInput}
        setRemainingQuestions={context.setRemainingQuestions}
        prefillScaleState={context.prefillScaleState}
      />
    )}
  </InputConsumer>
);

export default GetPropsFromProvider;
